import AnchorRegistryService from './AnchorRegistryService.js';
import TimelineInterpolationService from './TimelineInterpolationService.js';

/**
 * Task Date Calculation Service
 * Enhanced with Timeline Interpolation for flexible min/max bounds calculation
 * 
 * Two calculation modes:
 * 1. Interpolation Mode (NEW): Uses min/max bounds with student timeline position
 * 2. Legacy Mode: Uses timeline pressure categories (fallback)
 */
class TaskDateCalculationService {

  /**
   * Main method: Calculate final due date for a task
   * NEW: Checks for min/max bounds first, falls back to legacy system
   */
  static async calculateDueDate(template, student, college = null) {
    console.log(`\n🔥🔥🔥 [CRITICAL PATH DEBUG] 🔥🔥🔥`);
    console.log(`[TaskDateCalculation] ===== CORE CALCULATION SERVICE CALLED =====`);
    console.log(`[TaskDateCalculation] 🚨 THIS IS THE MAIN ENTRY POINT 🚨`);
    console.log(`[TaskDateCalculation] TODAY'S DATE: ${new Date().toISOString().split('T')[0]}`);
    
    // Critical debugging for August 26 bug
    console.log(`[TaskDateCalculation] 📋 TEMPLATE ANALYSIS:`);
    console.log(`[TaskDateCalculation]   Title: "${template.title}"`);
    console.log(`[TaskDateCalculation]   ID: ${template.id}`);
    console.log(`[TaskDateCalculation]   Category: ${template.category}`);
    console.log(`[TaskDateCalculation]   Anchor: ${template.anchor_id || 'null'}`);
    console.log(`[TaskDateCalculation]   Legacy offset: ${template.offset_days || 'null'} days`);
    console.log(`[TaskDateCalculation]   Minimum lead days: ${template.minimum_lead_days || 'null'}`);
    
    console.log(`[TaskDateCalculation] 🎯 BOUNDS ANALYSIS:`);
    console.log(`[TaskDateCalculation]   max_rd_offset_days: ${template.max_rd_offset_days}`);
    console.log(`[TaskDateCalculation]   min_rd_offset_days: ${template.min_rd_offset_days}`);
    console.log(`[TaskDateCalculation]   max_ed_offset_days: ${template.max_ed_offset_days}`);
    console.log(`[TaskDateCalculation]   min_ed_offset_days: ${template.min_ed_offset_days}`);
    
    console.log(`[TaskDateCalculation] 👤 STUDENT ANALYSIS:`);
    console.log(`[TaskDateCalculation]   Student Name: ${student.name || 'Student'}`);
    console.log(`[TaskDateCalculation]   Student ID: ${student.id || 'null'}`);
    console.log(`[TaskDateCalculation]   Student Timeline baseline: ${student.timeline_baseline_date || 'null'}`);
    console.log(`[TaskDateCalculation]   Student Created at: ${student.created_at || 'null'}`);
    console.log(`[TaskDateCalculation]   Student Graduation year: ${student.graduation_year || 'null'}`);
    console.log(`[TaskDateCalculation]   Student Timeline pressure: ${student.timeline_pressure || 'null'}`);
    
    console.log(`[TaskDateCalculation] 🏫 COLLEGE ANALYSIS:`);
    if (college) {
      console.log(`[TaskDateCalculation]   Name: ${college.name || 'Unknown'}`);
      console.log(`[TaskDateCalculation]   Application round: ${college.application_round || 'null'}`);
      console.log(`[TaskDateCalculation]   College object type: ${typeof college}`);
      console.log(`[TaskDateCalculation]   Has global_colleges? ${college.global_colleges ? 'yes' : 'no'}`);
      if (college.global_colleges) {
        console.log(`[TaskDateCalculation]   ED deadline: ${college.global_colleges.ed_deadline || 'null'}`);
        console.log(`[TaskDateCalculation]   RD deadline: ${college.global_colleges.rd_deadline || 'null'}`);
      }
    } else {
      console.log(`[TaskDateCalculation]   College: null (global milestone)`);
    }

    try {
      // Check if template has min/max bounds configured
      const hasBounds = this.templateHasBounds(template);
      
      console.log(`[TaskDateCalculation] 🔀 CALCULATION PATH SELECTION:`);
      console.log(`[TaskDateCalculation]   Has bounds: ${hasBounds}`);
      
      if (hasBounds) {
        console.log(`[TaskDateCalculation] 🚀 USING NEW INTERPOLATION SYSTEM`);
        console.log(`[TaskDateCalculation] 🚀 This path should hit comprehensive debug logging`);
        const result = await this.calculateWithInterpolation(template, student, college);
        
        // Critical validation for August 26 bug at the service level
        const studentStartDate = new Date(student.timeline_baseline_date || student.created_at);
        const isBeforeStart = result < studentStartDate;
        const daysDifference = Math.round((result - studentStartDate) / (1000 * 60 * 60 * 24));
        
        console.log(`[TaskDateCalculation] 🚨 SERVICE LEVEL VALIDATION:`);
        console.log(`[TaskDateCalculation]   Result date: ${result.toISOString().split('T')[0]}`);
        console.log(`[TaskDateCalculation]   Student start: ${studentStartDate.toISOString().split('T')[0]}`);
        console.log(`[TaskDateCalculation]   Days difference: ${daysDifference}`);
        console.log(`[TaskDateCalculation]   Valid? ${isBeforeStart ? '❌ AUGUST 26 BUG!' : '✅ Valid'}`);
        
        if (isBeforeStart) {
          console.error(`[TaskDateCalculation] 🔥🔥🔥 AUGUST 26 BUG CAUGHT IN SERVICE! 🔥🔥🔥`);
          console.error(`[TaskDateCalculation] Template "${template.title}" produced invalid date!`);
        }
        
        return result;
      } else {
        console.log(`[TaskDateCalculation] 📜 USING LEGACY PRESSURE SYSTEM`);
        console.log(`[TaskDateCalculation] ⚠️  This may not have comprehensive debugging`);
        return await this.calculateWithLegacySystem(template, student, college);
      }

    } catch (error) {
      console.error(`[TaskDateCalculation] 🔥🔥🔥 ERROR IN CORE CALCULATION SERVICE 🔥🔥🔥`);
      console.error(`[TaskDateCalculation] Error details:`, error);
      console.error(`[TaskDateCalculation] Error stack:`, error.stack);
      console.error(`[TaskDateCalculation] Template causing error: "${template.title}" (ID: ${template.id})`);

      // Fallback: use a reasonable default
      const fallbackDate = new Date();
      const fallbackDays = template.minimum_lead_days || 7;
      fallbackDate.setDate(fallbackDate.getDate() + fallbackDays);

      console.error(`[TaskDateCalculation] 💥 FALLING BACK TO: today + ${fallbackDays} days = ${fallbackDate.toISOString().split('T')[0]}`);
      return fallbackDate;
    } finally {
      console.log(`[TaskDateCalculation] ===== END CORE CALCULATION SERVICE =====\n`);
    }
  }

  /**
   * Check if template has min/max bounds configured
   */
  static templateHasBounds(template) {
    return (
      (template.max_rd_offset_days !== null && template.min_rd_offset_days !== null) ||
      (template.max_ed_offset_days !== null && template.min_ed_offset_days !== null)
    );
  }

  /**
   * NEW: Calculate due date using interpolation system
   */
  static async calculateWithInterpolation(template, student, college = null) {
    console.log(`[TaskDateCalculation] 🚀 Starting interpolation calculation`);

    // Use the TimelineInterpolationService for the calculation
    const dueDate = await TimelineInterpolationService.calculateInterpolatedDueDate(
      template,
      student,
      college
    );

    // Apply remaining steps from legacy system (calendar adjustments, etc.)
    const adjustedDate = this.applyCalendarAdjustments(dueDate, template);

    // No longer enforcing minimum lead time - let interpolation work as designed
    const finalDate = adjustedDate;

    // Validate the final date before returning
    if (isNaN(finalDate.getTime())) {
      console.error(`[TaskDateCalculation] ❌ Invalid final date calculated for template: ${template.title}`);
      console.error(`[TaskDateCalculation] Debug info:`, {
        templateId: template.id,
        templateTitle: template.title,
        anchorId: template.anchor_id,
        finalDate: finalDate.toString(),
        isValidDate: !isNaN(finalDate.getTime())
      });

      // Return a fallback date (30 days from now) instead of crashing
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() + 30);
      console.log(`[TaskDateCalculation] 🔧 Using fallback date: ${fallbackDate.toISOString().split('T')[0]}`);
      return fallbackDate;
    }

    console.log(`[TaskDateCalculation] 🎯 INTERPOLATION RESULT: ${finalDate.toISOString().split('T')[0]}`);
    return finalDate;
  }

  /**
   * LEGACY: Calculate due date using original 7-step system
   */
  static async calculateWithLegacySystem(template, student, college = null) {
    console.log(`[TaskDateCalculation] 📜 Starting legacy 7-step calculation`);
    
    // Step 1: Get base anchor date from registry
    const baseAnchorDate = await AnchorRegistryService.calculateAnchorDate(
      template.anchor_id,
      student,
      college,
      template.depends_on_template_id
    );
    console.log(`[TaskDateCalculation] Step 1 - Base anchor: ${baseAnchorDate.toISOString().split('T')[0]}`);

    // Step 2: Apply base offset from template
    const offsetDate = this.applyBaseOffset(baseAnchorDate, template);
    console.log(`[TaskDateCalculation] Step 2 - After base offset: ${offsetDate.toISOString().split('T')[0]}`);

    // Step 3: Apply timeline pressure compression (with JSON modifiers)
    const compressedDate = this.applyTimelinePressureCompression(
      offsetDate, 
      template, 
      student.timeline_pressure || 'steady'
    );
    console.log(`[TaskDateCalculation] Step 3 - After pressure compression: ${compressedDate.toISOString().split('T')[0]}`);

    // Step 4: Apply application round modifiers (with JSON modifiers)
    const roundAdjustedDate = this.applyApplicationRoundModifiers(
      compressedDate, 
      template, 
      college?.application_round
    );
    console.log(`[TaskDateCalculation] Step 4 - After round modifiers: ${roundAdjustedDate.toISOString().split('T')[0]}`);

    // Step 5: Apply ED priority bumping if applicable
    const priorityAdjustedDate = this.applyEDPriorityBumping(
      roundAdjustedDate, 
      template, 
      college?.application_round === 'early_decision' || college?.application_round === 'early_decision_2'
    );
    console.log(`[TaskDateCalculation] Step 5 - After ED priority: ${priorityAdjustedDate.toISOString().split('T')[0]}`);

    // Step 6: Apply calendar adjustments (weekends, business days)
    const calendarAdjustedDate = this.applyCalendarAdjustments(priorityAdjustedDate, template);
    console.log(`[TaskDateCalculation] Step 6 - After calendar adjustments: ${calendarAdjustedDate.toISOString().split('T')[0]}`);

    // Step 7: No longer enforcing minimum lead time - let the offsets work as designed
    const finalDate = calendarAdjustedDate;
    console.log(`[TaskDateCalculation] Step 7 - Final date: ${finalDate.toISOString().split('T')[0]}`);
    
    // Final comparison summary
    console.log(`[TaskDateCalculation] 🎯 LEGACY RESULT SUMMARY:`);
    console.log(`[TaskDateCalculation] Template: "${template.title}"`);
    console.log(`[TaskDateCalculation] Student: ${student.name || 'Student'}`);
    console.log(`[TaskDateCalculation] Anchor Resolution: ${template.anchor_id} → ${baseAnchorDate.toISOString().split('T')[0]}`);
    console.log(`[TaskDateCalculation] Pressure Conversion: ${template.offset_days}d (${template.native_pressure || 'comprehensive'}) → adjusted for ${student.timeline_pressure || 'comprehensive'}`);
    console.log(`[TaskDateCalculation] FINAL MILESTONE DATE: ${finalDate.toISOString().split('T')[0]}`);
    console.log(`[TaskDateCalculation] ⚠️  Consider migrating this template to bounds system!`);
    console.log(`[TaskDateCalculation] ===== END LEGACY CALCULATION =====\n`);
    
    return finalDate;
  }

  /**
   * Step 2: Apply base offset from template
   * Now uses signed offsets: negative = before, positive = after
   */
  static applyBaseOffset(anchorDate, template) {
    const baseDate = new Date(anchorDate);
    const offsetDays = template.offset_days || 0;

    // Simply add the signed offset
    baseDate.setDate(baseDate.getDate() + offsetDays);

    return baseDate;
  }

  /**
   * Step 3: Apply timeline pressure compression using bidirectional native pressure system
   */
  static applyTimelinePressureCompression(baseDate, template, timelinePressure) {
    // Check for template-specific pressure modifiers first (legacy support)
    if (template.timeline_pressure_modifier && template.timeline_pressure_modifier[timelinePressure]) {
      const pressureModifier = template.timeline_pressure_modifier[timelinePressure];
      console.log(`[TaskDateCalculation] Using JSON pressure modifier for ${timelinePressure}:`, pressureModifier);
      
      const modifiedDate = new Date(baseDate);
      const newOffset = pressureModifier.offset_days || template.offset_days;
      const offsetDifference = (template.offset_days || 0) - newOffset;
      
      // Simply apply the difference (signed offset handles direction)
      modifiedDate.setDate(modifiedDate.getDate() + offsetDifference);
      
      return modifiedDate;
    }
    
    // Use bidirectional native pressure system if available
    if (template.native_pressure) {
      const adjustedOffset = this.calculatePressureOffset(template, timelinePressure);
      const originalOffset = template.offset_days || 0;
      const offsetDifference = originalOffset - adjustedOffset;
      
      const adjustedDate = new Date(baseDate);
      
      // Apply the offset difference (signed offset handles direction)
      if (offsetDifference !== 0) {
        const sign = Math.sign(template.offset_days || 0);
        adjustedDate.setDate(adjustedDate.getDate() + (offsetDifference * sign));
        console.log(`[TaskDateCalculation] Bidirectional pressure: ${originalOffset}d (${template.native_pressure}) → ${adjustedOffset}d (${timelinePressure})`);
      } else {
        console.log(`[TaskDateCalculation] No pressure adjustment needed`);
      }
      
      return adjustedDate;
    }
    
    // Fallback to standard compression rates for templates without native_pressure
    const compressionRates = {
      'crisis': 0.3,      // 70% compression - very aggressive
      'intensive': 0.5,   // 50% compression - aggressive  
      'steady': 0.75,     // 25% compression - moderate
      'comprehensive': 1.0 // No compression - relaxed
    };
    
    const compressionRate = compressionRates[timelinePressure] || 0.75;
    const originalOffset = template.offset_days || 0;
    const compressedOffset = Math.max(
      Math.floor(originalOffset * compressionRate),
      template.minimum_lead_days || 3
    );
    
    const compressedDate = new Date(baseDate);
    const offsetAdjustment = originalOffset - compressedOffset;
    
    // Apply compression adjustment (signed offset handles direction)
    if (offsetAdjustment > 0) {
      const sign = Math.sign(template.offset_days || 0);
      compressedDate.setDate(compressedDate.getDate() + (offsetAdjustment * sign));
      console.log(`[TaskDateCalculation] Standard compression: ${originalOffset} → ${compressedOffset} days`);
    } else {
      console.log(`[TaskDateCalculation] No pressure compression needed`);
    }
    
    return compressedDate;
  }

  /**
   * Calculate pressure-adjusted offset using bidirectional system
   * Same logic as Timeline Builder for consistency
   */
  static calculatePressureOffset(template, targetPressure) {
    // Timeline Pressure Modifiers (base units for calculation)
    const pressureModifiers = {
      'crisis': 3,
      'intensive': 5, 
      'steady': 7,
      'comprehensive': 14
    };
    
    // Application Round Modifiers (absolute minimums)
    const roundModifiers = {
      'ed_deadline': 60,
      'ea_deadline': 60,
      'rd_deadline': 3
    };
    
    const nativePressure = template.native_pressure || 'comprehensive';
    const nativeOffset = template.offset_days;
    
    // Calculate offset for target pressure based on native values
    const nativePressureValue = pressureModifiers[nativePressure];
    const targetPressureValue = pressureModifiers[targetPressure];
    
    // Scale the offset based on pressure ratio
    const scaleFactor = targetPressureValue / nativePressureValue;
    let adjustedOffset = Math.max(1, Math.round(nativeOffset * scaleFactor));
    
    // Apply application round modifiers if applicable (absolute minimums)
    if (roundModifiers[template.anchor_id] && template.category === 'application') {
      adjustedOffset = Math.max(adjustedOffset, roundModifiers[template.anchor_id]);
    }
    
    console.log(`[TaskDateCalculation] Pressure conversion: ${nativeOffset}d (${nativePressure}) → ${adjustedOffset}d (${targetPressure})`);
    return adjustedOffset;
  }

  /**
   * Step 4: Apply application round modifiers (with JSON modifiers)
   */
  static applyApplicationRoundModifiers(baseDate, template, applicationRound) {
    if (!applicationRound) {
      console.log(`[TaskDateCalculation] No application round specified`);
      return baseDate;
    }

    // Check for template-specific round modifiers
    if (template.application_round_modifier && template.application_round_modifier[applicationRound]) {
      const roundModifier = template.application_round_modifier[applicationRound];
      console.log(`[TaskDateCalculation] Using JSON round modifier for ${applicationRound}:`, roundModifier);
      
      const modifiedDate = new Date(baseDate);
      const newOffset = roundModifier.offset_days || template.offset_days;
      const offsetAdjustment = newOffset - (template.offset_days || 0);

      // Apply adjustment (positive or negative based on the adjustment value)
      modifiedDate.setDate(modifiedDate.getDate() + offsetAdjustment);

      return modifiedDate;
    }
    
    console.log(`[TaskDateCalculation] No round modifier for ${applicationRound}`);
    return baseDate;
  }

  /**
   * Step 5: Apply ED priority bumping
   */
  static applyEDPriorityBumping(baseDate, template, isEDSchool) {
    if (!isEDSchool || !template.ed_priority_bump_days || template.ed_priority_bump_days <= 0) {
      console.log(`[TaskDateCalculation] No ED priority bump needed`);
      return baseDate;
    }
    
    const priorityDate = new Date(baseDate);
    priorityDate.setDate(priorityDate.getDate() - template.ed_priority_bump_days);
    console.log(`[TaskDateCalculation] Applied ED priority bump: -${template.ed_priority_bump_days} days`);
    
    // Never schedule before today
    const today = new Date();
    if (priorityDate < today) {
      console.log(`[TaskDateCalculation] ED bump would schedule in past, using today`);
      return today;
    }
    
    return priorityDate;
  }

  /**
   * Step 6: Apply calendar adjustments (weekends, business days)
   */
  static applyCalendarAdjustments(baseDate, template) {
    let adjustedDate = new Date(baseDate);
    
    // Avoid weekends if specified
    if (template.avoid_weekends) {
      while (adjustedDate.getDay() === 0 || adjustedDate.getDay() === 6) {
        adjustedDate.setDate(adjustedDate.getDate() - 1);
      }
      console.log(`[TaskDateCalculation] Adjusted to avoid weekends`);
    }
    
    // Business days only calculation
    if (template.business_days_only) {
      adjustedDate = this.adjustForBusinessDays(adjustedDate, template.offset_days || 0);
      console.log(`[TaskDateCalculation] Adjusted for business days only`);
    }
    
    return adjustedDate;
  }

  /**
   * Step 7: Ensure minimum lead time validation
   */
  static enforceMinimumLeadTime(calculatedDate, minimumDays, template) {
    // Only enforce minimum lead time for certain anchor types
    // Timeline-anchored tasks should be allowed to schedule in the past
    const enforcementAnchors = ['today', 'from_creation', 'dynamic'];

    if (template?.anchor_id && !enforcementAnchors.includes(template.anchor_id)) {
      // For timeline_baseline, ed_deadline, rd_deadline, senior_year_start anchors,
      // trust the calculated date without enforcement
      console.log(`[TaskDateCalculation] Skipping minimum lead time for anchor: ${template.anchor_id}`);
      return calculatedDate;
    }

    // Original enforcement logic for dynamic tasks only
    const today = new Date();
    const minimumDate = new Date();
    minimumDate.setDate(today.getDate() + minimumDays);

    if (calculatedDate < minimumDate) {
      console.log(`[TaskDateCalculation] Enforcing minimum lead time: ${minimumDays} days for dynamic task`);
      return minimumDate;
    }

    console.log(`[TaskDateCalculation] Date meets minimum lead time requirement`);
    return calculatedDate;
  }

  /**
   * Helper: Adjust for business days only
   */
  static adjustForBusinessDays(date, offsetDays) {
    let adjustedDate = new Date(date);
    
    // If we don't have an offset, just make sure we're on a business day
    if (offsetDays === 0) {
      while (adjustedDate.getDay() === 0 || adjustedDate.getDay() === 6) {
        adjustedDate.setDate(adjustedDate.getDate() - 1);
      }
      return adjustedDate;
    }
    
    // Count business days for the offset
    let businessDaysAdded = 0;
    const direction = offsetDays > 0 ? 1 : -1;
    const targetDays = Math.abs(offsetDays);
    
    while (businessDaysAdded < targetDays) {
      adjustedDate.setDate(adjustedDate.getDate() + direction);
      
      // Skip weekends
      if (adjustedDate.getDay() !== 0 && adjustedDate.getDay() !== 6) {
        businessDaysAdded++;
      }
    }
    
    return adjustedDate;
  }

  /**
   * Calculate timeline pressure for a student
   * Simplified version of existing logic
   */
  static calculateTimelinePressure(student) {
    const today = new Date();
    const earliestDeadline = new Date(student.earliest_application_deadline || '2026-01-01');
    const daysUntilDeadline = Math.ceil((earliestDeadline - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDeadline < 30) return 'crisis';
    if (daysUntilDeadline < 60) return 'intensive'; 
    if (daysUntilDeadline < 120) return 'steady';
    return 'comprehensive';
  }

  /**
   * Preview what timeline pressure changes would do to dates
   */
  static async previewPressureImpact(template, student, college, newPressure) {
    const originalPressure = student.timeline_pressure;
    
    // Calculate with original pressure
    const originalDate = await this.calculateDueDate(template, student, college);
    
    // Calculate with new pressure
    const tempStudent = { ...student, timeline_pressure: newPressure };
    const newDate = await this.calculateDueDate(template, tempStudent, college);
    
    const daysDifference = Math.ceil((newDate - originalDate) / (1000 * 60 * 60 * 24));
    
    return {
      original_date: originalDate.toISOString().split('T')[0],
      new_date: newDate.toISOString().split('T')[0],
      days_difference: daysDifference,
      direction: daysDifference > 0 ? 'later' : daysDifference < 0 ? 'earlier' : 'same'
    };
  }

  /**
   * Bulk calculate due dates for multiple templates
   */
  static async calculateBulkDueDates(templates, student, college = null) {
    const results = [];
    
    for (const template of templates) {
      try {
        const dueDate = await this.calculateDueDate(template, student, college);
        results.push({
          template_id: template.id,
          title: template.title,
          due_date: dueDate.toISOString().split('T')[0],
          anchor_used: template.anchor_id,
          success: true
        });
      } catch (error) {
        console.error(`Failed to calculate due date for ${template.title}:`, error);
        results.push({
          template_id: template.id,
          title: template.title,
          due_date: null,
          anchor_used: template.anchor_id,
          success: false,
          error: error.message
        });
      }
    }
    
    return results;
  }
}

export default TaskDateCalculationService;